import { ErrorMessage, Formik, FormikHelpers as FormikActions } from 'formik'
import React from 'react'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import * as Yup from 'yup'
import { Role, ValidationError } from 'epc-schema'
import { Form, Indicators, Input } from 'ui'
import { OptionalString, RequiredEmail, RequiredString } from 'validations'
import {
  DistrictSelectField,
  DistrictSelectFn,
  SchoolSelectField,
  SchoolSelectFn,
} from '../FormControls'
import { useCreateProfile } from '../graphql'

/**
 * This interface represents potential form values and
 * is passed into our custom form field factories to get
 * type safety on our form.
 */
interface IInitialSignUpFormValues {
  [key: string]: string | undefined
  termsAndConditions?: string | undefined
  companyName?: string | undefined
  firstName?: string | undefined
  lastName?: string | undefined
  email?: string | undefined
  phone?: string | undefined
  userRole?: string | undefined
  schoolId?: string | undefined
  schoolName?: string | undefined
  districtId?: string | undefined
  districtName?: string | undefined
}

// tslint:disable object-literal-sort-keys
/**
 * This schema is used by formik to provide client side
 * form validation.
 */
const InitialSignupSchema = Yup.object().shape({
  termsAndConditions: RequiredString('Must be accepted'),
  companyName: OptionalString().when('userRole', {
    is: 'pro',
    then: RequiredString(),
    otherwise: OptionalString(),
  }),
  districtName: OptionalString().when('userRole', {
    is: 'educator',
    then: RequiredString(),
    otherwise: OptionalString(),
  }),
  emailAddress: RequiredEmail(),
  firstName: RequiredString(),
  lastName: RequiredString(),
  phone: RequiredString().min(14, 'Must be a valid phone number'),
  schoolName: OptionalString().when('userRole', {
    is: 'educator',
    then: RequiredString(),
    otherwise: OptionalString(),
  }),
  userRole: RequiredString(),
})
// tslint:enable object-literal-sort-keys
/* END - Form Values */

/* BEGIN - Custom Form Renderers */
const FormField = Input.Renderer<IInitialSignUpFormValues>()
const RadioGroupField = Input.RadioGroupRenderer<IInitialSignUpFormValues>()
const CheckBoxGroupField =
  Input.CheckBoxGroupRenderer<IInitialSignUpFormValues>()
const ErrorSummary = Form.ErrorSummaryRenderer<IInitialSignUpFormValues>()
/* END - Custom Form Renderers */

/**
 * The initial step for creating an account as a teacher.
 */
const FormComponent: React.FC<RouteComponentProps> = (
  props: RouteComponentProps
) => {
  const [createProfile, { error: mutationError }] = useCreateProfile()

  const handleSubmitForm = async (
    values: IInitialSignUpFormValues,
    actions: FormikActions<any>
  ) => {
    try {
      const result = await createProfile({
        variables: {
          profileInput: {
            companyName: values.companyName,
            districtId: values.districtId,
            districtName: values.districtName,
            emailAddress: values.emailAddress as string,
            firstName: values.firstName as string,
            lastName: values.lastName as string,
            phone: values.phone as string,
            role: values.userRole as Role,
            schoolId: values.schoolId,
            schoolName: values.schoolName,
          },
        },
      })
      if (
        result.data &&
        !result.data.response.success &&
        result.data.response.errors
      ) {
        result.data.response.errors
          .map((e) => e as ValidationError)
          .forEach((fieldError) =>
            actions.setFieldError(`${fieldError.path}`, fieldError.message)
          )
      } else {
        props.history.push('/signup/confirm')
      }
    } catch (e) {
      actions.setFieldError(
        '_',
        `There was a problem connecting to the server. Please check your internet connection. If all seems well, then it is likely your organization has blocked access to ${process.env.REACT_APP_EPC_API_URL}.  Please contact your IT department.`
      )
    }
    actions.setSubmitting(false)
  }

  return (
    <Formik
      initialValues={{
        companyName: '',
        districtId: '',
        districtName: '',
        emailAddress: '',
        firstName: '',
        lastName: '',
        phone: '',
        schoolId: '',
        schoolName: '',
        termsAndConditions: '',
        userRole: '',
      }}
      onSubmit={handleSubmitForm}
      validationSchema={InitialSignupSchema}
    >
      {({
        errors,
        isSubmitting,
        handleSubmit,
        values,
        setFieldValue,
        submitCount,
        submitForm,
      }) => {
        const onDismiss = (name: string) => () => null
        const selectDistrict: DistrictSelectFn = (district) => {
          setFieldValue('districtId', district.id)
          setFieldValue('districtName', district.name)
          setFieldValue('schoolId', '')
          setFieldValue('schoolName', '')
        }
        const selectSchool: SchoolSelectFn = (school) => {
          setFieldValue('schoolId', school.id)
          setFieldValue('schoolName', school.name)
        }
        const hasSchoolDetails =
          values.userRole === Role.Educator &&
          (values.schoolId || '').length > 0
        const hasProDetails = (values.companyName || '').length > 0
        const incomplete = values.userRole === ''
        return (
          <Form.Form onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Item width={[1, 3 / 7, 1 / 3]}>
                <FormField
                  disabled={isSubmitting}
                  name="firstName"
                  label="First Name"
                  type="text"
                  required={true}
                />
              </Form.Item>
              <Form.Item width={[1, 4 / 7, 2 / 3]}>
                <FormField
                  disabled={isSubmitting}
                  name="lastName"
                  label="Last Name"
                  type="text"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={1}>
                <FormField
                  disabled={isSubmitting}
                  name="emailAddress"
                  label="Email Address"
                  placeholder="my@emailaddress.com"
                  type="email"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={[1, 3 / 4]}>
                <FormField
                  disabled={isSubmitting}
                  name="phone"
                  label="Phone"
                  type="text"
                  component={Input.RendererComponent.Masked}
                  mask={Input.Masks.PhoneNumber}
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={[1]}>
                <RadioGroupField
                  disabled={isSubmitting}
                  inline={true}
                  name="userRole"
                  label="I am an..."
                  required={true}
                  container={Form.DecorativeCheckBox}
                  indicator={Form.DecorativeCheckboxIndicator}
                  values={[
                    { label: 'Educator', value: Role.Educator },
                    {
                      label: 'Industry Professional',
                      value: Role.Professional,
                    },
                  ]}
                />
              </Form.Item>
            </Form.Row>
            {values.userRole === Role.Educator ? (
              <Form.Row>
                <Form.Item width={1}>
                  <DistrictSelectField
                    disabled={isSubmitting}
                    name="districtId"
                    label="School District"
                    value={values.districtName || ''}
                    placeholder="Choose your district"
                    onSelect={selectDistrict}
                    onDismiss={onDismiss('district')}
                  />
                </Form.Item>
              </Form.Row>
            ) : null}
            {values.userRole === Role.Professional ? (
              <Form.Row>
                <Form.Item width={1}>
                  <FormField
                    disabled={isSubmitting}
                    name="companyName"
                    label="Company"
                    type="text"
                    required={true}
                  />
                </Form.Item>
              </Form.Row>
            ) : null}
            {values.userRole === Role.Educator && values.districtId ? (
              <Form.Row>
                <Form.Item width={1}>
                  <SchoolSelectField
                    disabled={isSubmitting}
                    name="schoolId"
                    districtId={values.districtId}
                    label="School"
                    value={values.schoolName || ''}
                    placeholder="Choose your school"
                    onSelect={selectSchool}
                    onDismiss={onDismiss('school')}
                  />
                </Form.Item>
              </Form.Row>
            ) : null}
            {hasProDetails || hasSchoolDetails ? (
              <Form.Row>
                <Form.Item>
                  <CheckBoxGroupField
                    disabled={isSubmitting}
                    label="Terms and Conditions"
                    required={true}
                    name="termsAndConditions"
                    values={[
                      {
                        label:
                          values.userRole === Role.Educator
                            ? 'I attest that I am an employed educator in an Arizona K-12 school and that I am using this Educator PRO Connect Tool to connect with industry professionals to support my students and myself for education purposes. I can terminate my account at any time which will result in deactivation or disablement of the account and access and the deletion of content collected using the Tool.  I understand that the Office of the Maricopa County School Superintendent will approve my account and can delete my account if I do not follow the intended purpose of the Educator PRO Connect Tool.'
                            : 'I attest that I am a volunteer industry professional and that I am using this Educator PRO Connect Tool to support teachers and students in connecting with professionals for education purposes and not for sales of goods or services. I can terminate my account at any time which will result in deactivation or disablement of my account and the deletion of content I collected using the Tool.  I understand that the Office of the Maricopa County School Superintendent will approve my account and can delete my account if I do not follow the intended education purposes of the Educator PRO Connect Tool.',
                        value: 'true',
                      },
                    ]}
                  />
                  <ErrorMessage
                    name="termsAndConditions"
                    component={Input.Error}
                  />
                </Form.Item>
              </Form.Row>
            ) : null}
            <Form.ErrorComponent
              error={mutationError ? mutationError.message : null}
            />
            <Form.GeneralError name="_" />
            {submitCount > 0 ? <ErrorSummary errors={errors} /> : null}
            <Form.Row>
              <Form.Item width={1}>
                <Form.Button
                  type="submit"
                  disabled={isSubmitting || incomplete}
                  align="right"
                  onClick={submitForm}
                >
                  {isSubmitting ? (
                    <Indicators.Spinner
                      size={'lg'}
                      className="mt-auto mr-2 mb-auto ml-auto"
                    />
                  ) : (
                    ''
                  )}
                  <span>Create My Account</span>
                </Form.Button>
              </Form.Item>
            </Form.Row>
          </Form.Form>
        )
      }}
    </Formik>
  )
}

export const InitialSignUp = withRouter(FormComponent)
